// Affiliates module — manage referral partners, their commission agreements,
// which customers they're attributed to, and the monthly payment runs.
//
// Three tabs:
//   1. Partners      — CRUD list, each with active agreement summary
//   2. Attributions  — assign customers to partners, end attributions
//   3. Payments      — generate / approve / mark-paid / download backup

import { useMemo, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card } from '../components/Card'
import { Icon } from '../components/Icon'
import { DateRangePicker } from '../components/DateRangePicker'
import { DownloadLink } from '../components/DownloadLink'
import { useTranslation } from 'react-i18next'

// ============================================================
// Types
// ============================================================
interface Partner {
  id: number
  name: string
  contact_email: string | null
  contact_phone: string | null
  company: string | null
  country: string | null
  payment_method: string
  payment_destination: unknown
  tax_id: string | null
  tax_form_type: string | null
  active: boolean
  notes: string | null
}

interface Agreement {
  id: number
  partner_id: number
  model_type: 'profit_pct' | 'per_label_cents'
  rate_value: string
  profit_definition: string
  used_label_rule: string
  exclude_carriers: string[] | null
  minimum_payout_cents: number
  payout_currency: string
  effective_start: string
  effective_end: string | null
  notes: string | null
}

interface Attribution {
  id: number
  customer_name: string
  partner_id: number
  partner_name: string
  attributed_start: string
  attributed_end: string | null
  attribution_source: string | null
  notes: string | null
}

interface Payment {
  id: number
  partner_id: number
  partner_name: string
  agreement_id: number
  period_start: string
  period_end: string
  model_type_applied: string
  rate_applied: string
  gross_amount_cents: number
  held_below_minimum_cents: number
  payable_amount_cents: number
  payable_usd: number
  gross_usd: number
  held_usd: number
  currency: string
  customer_count: number
  shipment_count: number
  status: 'pending' | 'approved' | 'paid' | 'voided'
  generated_at: string
  approved_at: string | null
  paid_at: string | null
  payment_reference: string | null
  xlsx_path: string | null
}

interface PaymentLine {
  id: number
  customer_name: string
  metric_type: string
  units: string
  rate: string
  amount_cents: number
  shipment_count: number
}

interface Installment {
  id: number
  payment_id: number
  transfer_type: string
  amount_cents: number
  reference: string
  paid_at: string
  paid_by: string | null
  channel: string
  notes: string | null
}

interface ScorecardRow {
  partner_id: number
  partner_name: string
  active: boolean
  payment_method: string
  active_customer_count: number
  lifetime_paid_usd: number
  unpaid_balance_usd: number
  last_paid_at: string | null
}

interface BulkMonthResult {
  month: string
  ok: boolean
  skipped?: boolean
  partners_processed?: number
  total_payable_cents?: number
  detail?: string
}
interface BulkResult {
  months_processed: number
  months_ok: number
  months_skipped: number
  months_failed: number
  dry_run: boolean
  results: BulkMonthResult[]
}

// ============================================================
// Helpers
// ============================================================
function todayIso(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function firstOfThisMonthIso(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}
function firstOfLastMonthIso(): string {
  const d = new Date()
  d.setMonth(d.getMonth() - 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}
function lastOfLastMonthIso(): string {
  const d = new Date()
  d.setDate(0)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function addOneDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  const dt = new Date(y, m - 1, d + 1)
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`
}
function usd(n: number | null | undefined): string {
  if (n == null) return '$0.00'
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}
function statusPill(s: string) {
  const map: Record<string, string> = {
    pending:  'bg-amber-100 text-amber-800',
    approved: 'bg-royal-100 text-royal-800',
    paid:     'bg-mint-100 text-mint-800',
    voided:   'bg-ink-100 text-ink-600 line-through',
  }
  return `inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold ${map[s] ?? 'bg-ink-100 text-ink-700'}`
}


// ============================================================
// Tiny shared bits
// ============================================================
const inp = 'h-9 px-2.5 rounded-lg border border-ink-200 text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-mint-300'

function Field({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <div className={className}>
      <div className="text-[11px] uppercase tracking-wider text-ink-500 font-semibold mb-1">{label}</div>
      {children}
    </div>
  )
}

// ============================================================
// Main view
// ============================================================
type Tab = 'partners' | 'attributions' | 'payments'

export function AffiliatesView() {
  const { t } = useTranslation()
  const [tab, setTab] = useState<Tab>('partners')

  return (
    <div>
      <div className="mb-6">
        <div className="text-[12px] font-semibold text-mint-600 uppercase tracking-wider">Module 8</div>
        <h1 className="text-[32px] font-bold tracking-tight mt-2">Affiliates</h1>
        <p className="text-ink-500 text-[14px] mt-2">
          Manage referral partners, attribute customers to them, generate monthly commission payouts.
        </p>
      </div>

      <ScorecardStrip />

      <div className="flex items-center gap-1 mb-4 border-b border-ink-200">
        {([
          ['partners',     'Partners',     'users'],
          ['attributions', 'Attributions', 'arrow-right-left'],
          ['payments',     'Payments',     'badge-dollar-sign'],
        ] as [Tab, string, string][]).map(([k, label, icon]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`px-4 h-10 text-[13px] font-semibold border-b-2 -mb-px inline-flex items-center gap-1.5 ${
              tab === k ? 'border-mint-500 text-mint-700' : 'border-transparent text-ink-500 hover:text-ink-800'
            }`}
          >
            <Icon name={icon as 'users'} className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {tab === 'partners'     && <PartnersTab />}
      {tab === 'attributions' && <AttributionsTab />}
      {tab === 'payments'     && <PaymentsTab />}
    </div>
  )
}

// ============================================================
// Scorecard strip (always visible at top)
// ============================================================
function ScorecardStrip() {
  const sc = useQuery<{ partners: ScorecardRow[] }>({
    queryKey: ['affiliates', 'scorecard'],
    queryFn: () => fetch('/api/affiliates/scorecard').then(r => r.json()),
    staleTime: 30_000,
  })
  const rows = sc.data?.partners ?? []
  const totals = {
    partners: rows.length,
    active:   rows.filter(r => r.active).length,
    customers: rows.reduce((a, r) => a + r.active_customer_count, 0),
    unpaid:   rows.reduce((a, r) => a + r.unpaid_balance_usd, 0),
    paid:     rows.reduce((a, r) => a + r.lifetime_paid_usd, 0),
  }
  return (
    <Card className="p-4 mb-6 grid grid-cols-2 md:grid-cols-5 gap-4">
      <Metric label="Partners (active)" value={`${totals.active} / ${totals.partners}`} />
      <Metric label="Attributed customers" value={String(totals.customers)} />
      <Metric label="Unpaid balance" value={usd(totals.unpaid)} accent="amber" />
      <Metric label="Lifetime paid" value={usd(totals.paid)} accent="mint" />
      <Metric label="Last update" value={sc.dataUpdatedAt ? new Date(sc.dataUpdatedAt).toLocaleTimeString() : '—'} />
    </Card>
  )
}
function Metric({ label, value, accent }: { label: string; value: string; accent?: 'mint' | 'amber' }) {
  const v = accent === 'mint'  ? 'text-mint-700'
          : accent === 'amber' ? 'text-amber-700'
          : 'text-ink-900'
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-ink-500 font-semibold">{label}</div>
      <div className={`text-[22px] font-bold mt-1 ${v}`}>{value}</div>
    </div>
  )
}

// ============================================================
// PARTNERS TAB
// ============================================================
function PartnersTab() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [showAdd, setShowAdd] = useState(false)

  const partners = useQuery<{ partners: Partner[] }>({
    queryKey: ['affiliates', 'partners'],
    queryFn: () => fetch('/api/affiliates/partners').then(r => r.json()),
  })
  const agreements = useQuery<{ agreements: Agreement[] }>({
    queryKey: ['affiliates', 'agreements'],
    queryFn: () => fetch('/api/affiliates/agreements').then(r => r.json()),
  })

  const currentByPartner = useMemo(() => {
    const m = new Map<number, Agreement>()
    for (const a of agreements.data?.agreements ?? []) {
      if (a.effective_end == null && !m.has(a.partner_id)) m.set(a.partner_id, a)
    }
    return m
  }, [agreements.data])

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-[16px] font-semibold">Partners</h2>
        <button
          onClick={() => setShowAdd(s => !s)}
          className="h-9 px-3 rounded-lg bg-mint-500 text-white font-semibold inline-flex items-center gap-1.5 text-[13px]"
        >
          <Icon name="plus" className="w-4 h-4" /> Add partner
        </button>
      </div>

      {showAdd && <PartnerForm onClose={() => setShowAdd(false)} onSaved={() => {
        qc.invalidateQueries({ queryKey: ['affiliates', 'partners'] })
        qc.invalidateQueries({ queryKey: ['affiliates', 'scorecard'] })
        setShowAdd(false)
      }} />}

      <Card className="overflow-hidden">
        <table className="w-full text-[13px]">
          <thead className="bg-ink-50 text-ink-600 text-left">
            <tr>
              <th className="px-3 py-2 font-semibold">Name</th>
              <th className="px-3 py-2 font-semibold">Email</th>
              <th className="px-3 py-2 font-semibold">Payment</th>
              <th className="px-3 py-2 font-semibold">Current agreement</th>
              <th className="px-3 py-2 font-semibold">Status</th>
              <th className="px-3 py-2 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(partners.data?.partners ?? []).map(p => {
              const ag = currentByPartner.get(p.id)
              return (
                <tr key={p.id} className="border-t border-ink-100">
                  <td className="px-3 py-2">
                    <div className="font-semibold text-ink-900">{p.name}</div>
                    {p.company && <div className="text-[11px] text-ink-500">{p.company}</div>}
                  </td>
                  <td className="px-3 py-2 text-ink-700">{p.contact_email ?? '—'}</td>
                  <td className="px-3 py-2 text-ink-700">{p.payment_method}</td>
                  <td className="px-3 py-2">
                    {ag ? (
                      <span>
                        {ag.model_type === 'profit_pct'
                          ? `${parseFloat(ag.rate_value)}% of profit`
                          : `${parseFloat(ag.rate_value)}¢ per used label`}
                        <span className="text-[11px] text-ink-500 ml-2">since {String(ag.effective_start).slice(0, 10)}</span>
                      </span>
                    ) : (
                      <span className="text-ink-400">no agreement</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className={p.active ? 'text-mint-700' : 'text-ink-400'}>
                      {p.active ? 'active' : 'inactive'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <AgreementButton partner={p} onSaved={() => {
                      qc.invalidateQueries({ queryKey: ['affiliates', 'agreements'] })
                    }} />
                  </td>
                </tr>
              )
            })}
            {!partners.data?.partners.length && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-ink-400">
{t('affiliates.no_partners_yet')}
              </td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  )
}

function PartnerForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [company, setCompany] = useState('')
  const [country, setCountry] = useState('US')
  const [paymentMethod, setPaymentMethod] = useState('manual')
  const [paymentDestText, setPaymentDestText] = useState('')
  const [taxId, setTaxId] = useState('')
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function save() {
    setBusy(true); setErr(null)
    let dest: unknown = null
    if (paymentDestText.trim()) {
      try { dest = JSON.parse(paymentDestText) }
      catch { setErr('Payment destination must be valid JSON'); setBusy(false); return }
    }
    const r = await fetch('/api/affiliates/partners', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name, contact_email: email || null, contact_phone: phone || null,
        company: company || null, country: country || null,
        payment_method: paymentMethod,
        payment_destination: dest,
        tax_id: taxId || null,
        notes: notes || null,
      }),
    })
    setBusy(false)
    if (!r.ok) { setErr(`HTTP ${r.status}`); return }
    onSaved()
  }

  return (
    <Card className="p-4">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Name *"><input className={inp} value={name} onChange={e => setName(e.target.value)} /></Field>
        <Field label="Company"><input className={inp} value={company} onChange={e => setCompany(e.target.value)} /></Field>
        <Field label="Email"><input className={inp} value={email} onChange={e => setEmail(e.target.value)} /></Field>
        <Field label="Phone"><input className={inp} value={phone} onChange={e => setPhone(e.target.value)} /></Field>
        <Field label="Country"><input className={inp} value={country} onChange={e => setCountry(e.target.value)} /></Field>
        <Field label="Payment method">
          <select className={inp} value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
            {['manual', 'stripe_connect', 'chase_ach', 'wire', 'paypal', 'check'].map(o =>
              <option key={o} value={o}>{o}</option>
            )}
          </select>
        </Field>
        <Field label="Tax ID (W9 / EIN / SSN)"><input className={inp} value={taxId} onChange={e => setTaxId(e.target.value)} /></Field>
        <Field label="Payment destination (JSON, e.g. {&quot;email&quot;:&quot;a@b.com&quot;})" className="col-span-2">
          <input className={inp} value={paymentDestText} onChange={e => setPaymentDestText(e.target.value)} placeholder='{"routing":"...", "account":"..."}' />
        </Field>
        <Field label="Notes" className="col-span-2">
          <textarea className={`${inp} h-16`} value={notes} onChange={e => setNotes(e.target.value)} />
        </Field>
      </div>
      {err && <div className="mt-3 text-rose-600 text-[13px]">{err}</div>}
      <div className="mt-3 flex justify-end gap-2">
        <button onClick={onClose} className="h-9 px-3 rounded-lg border border-ink-200 text-[13px]">Cancel</button>
        <button disabled={!name || busy} onClick={save}
          className="h-9 px-4 rounded-lg bg-mint-500 text-white font-semibold text-[13px] disabled:opacity-50">
          {busy ? 'Saving…' : 'Save partner'}
        </button>
      </div>
    </Card>
  )
}

function AgreementButton({ partner, onSaved }: { partner: Partner; onSaved: () => void }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button
        onClick={() => setOpen(o => !o)}
        className="h-8 px-3 rounded-lg border border-ink-200 text-[12.5px] inline-flex items-center gap-1 hover:bg-ink-50"
      >
        <Icon name="plus" className="w-3.5 h-3.5" /> New agreement
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/30 z-40 flex items-center justify-center" onClick={() => setOpen(false)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-xl w-[520px] p-5 shadow-xl">
            <div className="text-[15px] font-semibold mb-3">
              New agreement for <span className="text-mint-700">{partner.name}</span>
            </div>
            <AgreementForm partnerId={partner.id} onClose={() => setOpen(false)} onSaved={() => {
              onSaved(); setOpen(false)
            }} />
          </div>
        </div>
      )}
    </>
  )
}

function AgreementForm({ partnerId, onClose, onSaved }: { partnerId: number; onClose: () => void; onSaved: () => void }) {
  const [modelType, setModelType] = useState<'profit_pct' | 'per_label_cents'>('profit_pct')
  const [rateValue, setRateValue] = useState('10')
  const [effectiveStart, setEffectiveStart] = useState(firstOfThisMonthIso())
  const [minimumPayout, setMinimumPayout] = useState('0')
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function save() {
    setBusy(true); setErr(null)
    const r = await fetch('/api/affiliates/agreements', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        partner_id: partnerId,
        model_type: modelType,
        rate_value: Number(rateValue),
        effective_start: effectiveStart,
        minimum_payout_cents: Math.round(Number(minimumPayout) * 100),
        notes: notes || null,
      }),
    })
    setBusy(false)
    if (!r.ok) { setErr(`HTTP ${r.status}`); return }
    onSaved()
  }

  return (
    <div className="space-y-3">
      <Field label="Model">
        <select className={inp} value={modelType} onChange={e => setModelType(e.target.value as 'profit_pct' | 'per_label_cents')}>
          <option value="profit_pct">% of profit</option>
          <option value="per_label_cents">cents per used label</option>
        </select>
      </Field>
      <Field label={modelType === 'profit_pct' ? 'Rate (% of profit)' : 'Rate (cents per label)'}>
        <input className={inp} value={rateValue} onChange={e => setRateValue(e.target.value)} />
      </Field>
      <Field label="Effective start">
        <input type="date" className={inp} value={effectiveStart} onChange={e => setEffectiveStart(e.target.value)} />
      </Field>
      <Field label="Minimum payout (USD, hold balance below this)">
        <input className={inp} value={minimumPayout} onChange={e => setMinimumPayout(e.target.value)} />
      </Field>
      <Field label="Notes">
        <textarea className={`${inp} h-16`} value={notes} onChange={e => setNotes(e.target.value)} />
      </Field>
      <div className="text-[11px] text-ink-500">
        Any currently-open agreement for this partner will be auto-closed on the day before this effective_start.
      </div>
      {err && <div className="text-rose-600 text-[13px]">{err}</div>}
      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onClose} className="h-9 px-3 rounded-lg border border-ink-200 text-[13px]">Cancel</button>
        <button disabled={busy} onClick={save}
          className="h-9 px-4 rounded-lg bg-mint-500 text-white font-semibold text-[13px] disabled:opacity-50">
          {busy ? 'Saving…' : 'Save agreement'}
        </button>
      </div>
    </div>
  )
}

// ============================================================
// ATTRIBUTIONS TAB
// ============================================================
function AttributionsTab() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [showAdd, setShowAdd] = useState(false)
  const [filterPartner, setFilterPartner] = useState<number | ''>('')
  const [filterCustomer, setFilterCustomer] = useState('')
  const [activeOnly, setActiveOnly] = useState(true)

  const partners = useQuery<{ partners: Partner[] }>({
    queryKey: ['affiliates', 'partners'],
    queryFn: () => fetch('/api/affiliates/partners').then(r => r.json()),
  })
  const list = useQuery<{ attributions: Attribution[] }>({
    queryKey: ['affiliates', 'attributions', filterPartner, filterCustomer, activeOnly],
    queryFn: () => {
      const p = new URLSearchParams()
      if (filterPartner) p.set('partner_id', String(filterPartner))
      if (filterCustomer) p.set('customer', filterCustomer)
      p.set('active_only', String(activeOnly))
      return fetch('/api/affiliates/attributions?' + p.toString()).then(r => r.json())
    },
  })

  const endAttr = useMutation({
    mutationFn: async (id: number) => fetch(`/api/affiliates/attributions/${id}/end`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['affiliates', 'attributions'] })
      qc.invalidateQueries({ queryKey: ['affiliates', 'scorecard'] })
    },
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <select className={inp + ' w-56'} value={filterPartner}
          onChange={e => setFilterPartner(e.target.value ? Number(e.target.value) : '')}>
          <option value="">All partners</option>
          {(partners.data?.partners ?? []).map(p =>
            <option key={p.id} value={p.id}>{p.name}</option>
          )}
        </select>
        <input className={inp + ' w-64'} placeholder={t('affiliates.filter_by_customer')}
          value={filterCustomer} onChange={e => setFilterCustomer(e.target.value)} />
        <label className="inline-flex items-center gap-1 text-[13px]">
          <input type="checkbox" checked={activeOnly} onChange={e => setActiveOnly(e.target.checked)} />
          Active only
        </label>
        <button
          onClick={() => setShowAdd(s => !s)}
          className="ml-auto h-9 px-3 rounded-lg bg-mint-500 text-white font-semibold inline-flex items-center gap-1.5 text-[13px]"
        >
          <Icon name="plus" className="w-4 h-4" /> Add attribution
        </button>
      </div>

      {showAdd && <AttributionForm
        partners={partners.data?.partners ?? []}
        onClose={() => setShowAdd(false)}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ['affiliates', 'attributions'] })
          qc.invalidateQueries({ queryKey: ['affiliates', 'scorecard'] })
          setShowAdd(false)
        }}
      />}

      <Card className="overflow-hidden">
        <table className="w-full text-[13px]">
          <thead className="bg-ink-50 text-ink-600 text-left">
            <tr>
              <th className="px-3 py-2 font-semibold">Customer</th>
              <th className="px-3 py-2 font-semibold">Partner</th>
              <th className="px-3 py-2 font-semibold">Started</th>
              <th className="px-3 py-2 font-semibold">Ended</th>
              <th className="px-3 py-2 font-semibold">Source</th>
              <th className="px-3 py-2 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(list.data?.attributions ?? []).map(a => (
              <tr key={a.id} className="border-t border-ink-100">
                <td className="px-3 py-2 font-mono text-[12px]">{a.customer_name}</td>
                <td className="px-3 py-2">{a.partner_name}</td>
                <td className="px-3 py-2 text-ink-700">{a.attributed_start}</td>
                <td className="px-3 py-2 text-ink-700">{a.attributed_end ?? <em className="text-mint-700 not-italic">active</em>}</td>
                <td className="px-3 py-2 text-ink-500">{a.attribution_source ?? '—'}</td>
                <td className="px-3 py-2 text-right">
                  {a.attributed_end == null && (
                    <button
                      onClick={() => endAttr.mutate(a.id)}
                      className="h-8 px-3 rounded-lg border border-ink-200 text-[12.5px] hover:bg-ink-50">
                      End today
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!list.data?.attributions.length && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-ink-400">
                No attributions match the current filter.
              </td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  )
}

function AttributionForm({ partners, onClose, onSaved }: { partners: Partner[]; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation()
  const [customer, setCustomer] = useState('')
  const [partnerId, setPartnerId] = useState<number | ''>('')
  const [start, setStart] = useState(firstOfThisMonthIso())
  const [source, setSource] = useState('manual')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // Reuse the same customers endpoint as the invoice generator
  const customers = useQuery<{ customers: { customer_name: string; shipment_count: number }[] }>({
    queryKey: ['invoices', 'customers'],
    queryFn: () => fetch('/api/invoices/customers').then(r => r.json()),
    staleTime: 5 * 60_000,
  })

  async function save() {
    setBusy(true); setErr(null)
    const r = await fetch('/api/affiliates/attributions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        customer_name: customer,
        partner_id: partnerId,
        attributed_start: start,
        attribution_source: source,
      }),
    })
    setBusy(false)
    if (!r.ok) { setErr(`HTTP ${r.status}`); return }
    onSaved()
  }

  return (
    <Card className="p-4">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Customer">
          <input className={inp} list="customer-options" value={customer}
            onChange={e => setCustomer(e.target.value)} placeholder={t('affiliates.start_typing')} />
          <datalist id="customer-options">
            {(customers.data?.customers ?? []).map(c =>
              <option key={c.customer_name} value={c.customer_name}>{c.shipment_count} shipments</option>
            )}
          </datalist>
        </Field>
        <Field label="Partner">
          <select className={inp} value={partnerId} onChange={e => setPartnerId(e.target.value ? Number(e.target.value) : '')}>
            <option value="">Select partner…</option>
            {partners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <Field label="Attribution start"><input type="date" className={inp} value={start} onChange={e => setStart(e.target.value)} /></Field>
        <Field label="Source">
          <select className={inp} value={source} onChange={e => setSource(e.target.value)}>
            {['manual', 'signup_link', 'contract_import', 'campaign'].map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
      </div>
      <div className="mt-3 text-[11px] text-ink-500">
        If this customer is currently attributed to another partner, that attribution will be auto-ended on the day before this start date.
      </div>
      {err && <div className="mt-3 text-rose-600 text-[13px]">{err}</div>}
      <div className="mt-3 flex justify-end gap-2">
        <button onClick={onClose} className="h-9 px-3 rounded-lg border border-ink-200 text-[13px]">Cancel</button>
        <button disabled={!customer || !partnerId || busy} onClick={save}
          className="h-9 px-4 rounded-lg bg-mint-500 text-white font-semibold text-[13px] disabled:opacity-50">
          {busy ? 'Saving…' : 'Save attribution'}
        </button>
      </div>
    </Card>
  )
}

// ============================================================
// PAYMENTS TAB
// ============================================================
function PaymentsTab() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [periodStart, setPeriodStart] = useState(firstOfLastMonthIso())
  const [periodEndIncl, setPeriodEndIncl] = useState(lastOfLastMonthIso())
  const [partnerId, setPartnerId] = useState<number | ''>('')
  const [dryRun, setDryRun] = useState(false)
  const [genMsg, setGenMsg] = useState<string | null>(null)
  const [selected, setSelected] = useState<Payment | null>(null)

  // Bulk selection
  const [checkedIds, setCheckedIds] = useState<Set<number>>(new Set())
  function toggleCheck(id: number) {
    setCheckedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }
  function toggleAll(payments: Payment[]) {
    setCheckedIds(prev => prev.size === payments.length ? new Set() : new Set(payments.map(p => p.id)))
  }

  const bulkApprove = useMutation({
    mutationFn: async (ids: number[]) => {
      const r = await fetch('/api/affiliates/payments/bulk-approve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json() as Promise<{ count: number }>
    },
    onSuccess: (data) => {
      setGenMsg(`Approved ${data.count} payment(s).`)
      setCheckedIds(new Set())
      qc.invalidateQueries({ queryKey: ['affiliates', 'payments'] })
      qc.invalidateQueries({ queryKey: ['affiliates', 'scorecard'] })
    },
    onError: (e: Error) => setGenMsg('Error: ' + e.message),
  })

  const bulkVoid = useMutation({
    mutationFn: async (ids: number[]) => {
      const r = await fetch('/api/affiliates/payments/bulk-void', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json() as Promise<{ count: number }>
    },
    onSuccess: (data) => {
      setGenMsg(`Voided ${data.count} payment(s).`)
      setCheckedIds(new Set())
      qc.invalidateQueries({ queryKey: ['affiliates', 'payments'] })
      qc.invalidateQueries({ queryKey: ['affiliates', 'scorecard'] })
    },
    onError: (e: Error) => setGenMsg('Error: ' + e.message),
  })

  const [deleteConfirmPending, setDeleteConfirmPending] = useState(false)
  const bulkDelete = useMutation({
    mutationFn: async (ids: number[]) => {
      const r = await fetch('/api/affiliates/payments/bulk-delete', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json() as Promise<{ deleted_count: number }>
    },
    onSuccess: (data) => {
      setGenMsg(`Deleted ${data.deleted_count} voided payment(s).`)
      setCheckedIds(new Set())
      setDeleteConfirmPending(false)
      qc.invalidateQueries({ queryKey: ['affiliates', 'payments'] })
      qc.invalidateQueries({ queryKey: ['affiliates', 'scorecard'] })
    },
    onError: (e: Error) => { setGenMsg('Error: ' + e.message); setDeleteConfirmPending(false) },
  })

  // Bulk calc state
  const [bulkFrom, setBulkFrom] = useState('2026-01')
  const [bulkTo, setBulkTo] = useState(() => {
    const d = new Date(); d.setDate(0) // last day of prev month
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
  })
  const [bulkDryRun, setBulkDryRun] = useState(false)
  const [bulkMsg, setBulkMsg] = useState<BulkResult | null>(null)

  // Filter state for the payments table
  const [filterPartner, setFilterPartner] = useState('')
  const [filterStatus,  setFilterStatus]  = useState('')
  const [filterMonth,   setFilterMonth]   = useState('')

  // Clear selection when filters change
  const clearFilters = () => {
    setFilterPartner(''); setFilterStatus(''); setFilterMonth('')
    setCheckedIds(new Set()); setDeleteConfirmPending(false)
  }

  const partners = useQuery<{ partners: Partner[] }>({
    queryKey: ['affiliates', 'partners'],
    queryFn: () => fetch('/api/affiliates/partners').then(r => r.json()),
  })

  const list = useQuery<{ payments: Payment[] }>({
    queryKey: ['affiliates', 'payments'],
    queryFn: () => fetch('/api/affiliates/payments?limit=500').then(r => r.json()),
    staleTime: 10_000,
  })

  const filteredPayments = useMemo(() => {
    return (list.data?.payments ?? []).filter(p => {
      if (filterPartner && String(p.partner_id) !== filterPartner) return false
      if (filterStatus  && p.status !== filterStatus)              return false
      if (filterMonth   && p.period_start.slice(0, 7) !== filterMonth) return false
      return true
    })
  }, [list.data?.payments, filterPartner, filterStatus, filterMonth])

  const exportHref = useMemo(() => {
    const qs = new URLSearchParams()
    if (filterPartner) qs.set('partner_id',   filterPartner)
    if (filterStatus)  qs.set('status',        filterStatus)
    if (filterMonth)   qs.set('period_month',  filterMonth)
    const q = qs.toString()
    return `/api/affiliates/payments/export-xlsx${q ? '?' + q : ''}`
  }, [filterPartner, filterStatus, filterMonth])

  const deleteAllVoided = useMutation({
    mutationFn: async () => {
      const r = await fetch('/api/affiliates/payments/voided/all', { method: 'DELETE' })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json() as Promise<{ deleted_count: number }>
    },
    onSuccess: (data) => {
      setGenMsg(`Deleted ${data.deleted_count} voided payment(s).`)
      qc.invalidateQueries({ queryKey: ['affiliates', 'payments'] })
      qc.invalidateQueries({ queryKey: ['affiliates', 'scorecard'] })
    },
    onError: (e: Error) => setGenMsg('Error: ' + e.message),
  })

  const deletePayment = useMutation({
    mutationFn: async (id: number) => {
      const r = await fetch(`/api/affiliates/payments/${id}`, { method: 'DELETE' })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['affiliates', 'payments'] })
      qc.invalidateQueries({ queryKey: ['affiliates', 'scorecard'] })
    },
  })

  const gen = useMutation({
    mutationFn: async () => {
      const r = await fetch('/api/affiliates/payments/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          period_start: periodStart,
          period_end:   addOneDay(periodEndIncl),
          partner_id:   partnerId || undefined,
          dry_run:      dryRun,
        }),
      })
      const data = await r.json()
      if (r.status === 409) throw new Error(data.error)
      return data
    },
    onSuccess: (data) => {
      setGenMsg(data.ok === false
        ? `Error: ${data.error ?? 'unknown'}${data.tail ? '\n\n' + data.tail : ''}`
        : `Generated for ${data.partners_processed} partner(s).`)
      qc.invalidateQueries({ queryKey: ['affiliates', 'payments'] })
      qc.invalidateQueries({ queryKey: ['affiliates', 'scorecard'] })
    },
    onError: (e: Error) => setGenMsg('Error: ' + e.message),
  })

  const bulk = useMutation({
    mutationFn: async () => {
      const r = await fetch('/api/affiliates/payments/generate-bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ from_month: bulkFrom, to_month: bulkTo, dry_run: bulkDryRun }),
      })
      return r.json() as Promise<BulkResult>
    },
    onSuccess: (data) => {
      setBulkMsg(data)
      qc.invalidateQueries({ queryKey: ['affiliates', 'payments'] })
      qc.invalidateQueries({ queryKey: ['affiliates', 'scorecard'] })
    },
    onError: (e: Error) => setBulkMsg({ months_processed: 0, months_ok: 0, months_skipped: 0, months_failed: 1, dry_run: bulkDryRun, results: [{ month: '—', ok: false, detail: e.message }] }),
  })

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="text-[13px] font-semibold mb-3 text-ink-700">Generate monthly payouts</div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <div className="text-[11px] uppercase text-ink-500 font-semibold mb-1">Period</div>
            <DateRangePicker
              value={{ from: periodStart, to: periodEndIncl }}
              onChange={(v) => { setPeriodStart(v.from); setPeriodEndIncl(v.to) }}
            />
          </div>
          <div>
            <div className="text-[11px] uppercase text-ink-500 font-semibold mb-1">Partner</div>
            <select className={inp + ' w-56'} value={partnerId} onChange={e => setPartnerId(e.target.value ? Number(e.target.value) : '')}>
  <option value="">{t('affiliates.all_active_partners')}</option>
              {(partners.data?.partners ?? []).filter(p => p.active).map(p =>
                <option key={p.id} value={p.id}>{p.name}</option>
              )}
            </select>
          </div>
          <label className="inline-flex items-center gap-1 text-[13px] mb-2">
            <input type="checkbox" checked={dryRun} onChange={e => setDryRun(e.target.checked)} />
            Dry-run (no DB writes)
          </label>
          <div className="ml-auto flex items-center gap-2">
            {(list.data?.payments ?? []).some(p => p.status === 'voided') && (
              <button
                disabled={deleteAllVoided.isPending}
                onClick={() => {
                  if (!confirm('Delete all voided payments? This cannot be undone.')) return
                  setGenMsg(null); deleteAllVoided.mutate()
                }}
                className="h-9 px-3 rounded-lg border border-rose-300 text-rose-700 font-semibold inline-flex items-center gap-1.5 text-[13px] hover:bg-rose-50 disabled:opacity-50">
                <Icon name="x" className="w-4 h-4" />
                {deleteAllVoided.isPending ? 'Deleting…' : 'Delete all voided'}
              </button>
            )}
            <button
              disabled={gen.isPending}
              onClick={() => { setGenMsg(null); gen.mutate() }}
              className="h-9 px-4 rounded-lg bg-mint-500 text-white font-semibold inline-flex items-center gap-2 disabled:opacity-50 text-[13px]">
              {gen.isPending ? <Icon name="loader" className="w-4 h-4 animate-spin" /> : <Icon name="sparkles" className="w-4 h-4" />}
              {gen.isPending ? 'Calculating…' : 'Generate payouts'}
            </button>
          </div>
        </div>
        {genMsg && <pre className="mt-3 text-[12px] bg-ink-50 border border-ink-100 rounded p-3 whitespace-pre-wrap">{genMsg}</pre>}
      </Card>

      {/* ── Bulk calculate ─────────────────────────────────────────── */}
      <Card className="p-4">
        <div className="text-[13px] font-semibold mb-3 text-ink-700">Bulk calculate — all partners, multiple months</div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <div className="text-[11px] uppercase text-ink-500 font-semibold mb-1">From month</div>
            <input
              type="month"
              className={inp}
              value={bulkFrom}
              onChange={e => setBulkFrom(e.target.value)}
            />
          </div>
          <div>
            <div className="text-[11px] uppercase text-ink-500 font-semibold mb-1">To month</div>
            <input
              type="month"
              className={inp}
              value={bulkTo}
              onChange={e => setBulkTo(e.target.value)}
            />
          </div>
          <label className="inline-flex items-center gap-1 text-[13px] mb-2">
            <input type="checkbox" checked={bulkDryRun} onChange={e => setBulkDryRun(e.target.checked)} />
            Dry-run (no DB writes)
          </label>
          <div className="ml-auto">
            <button
              disabled={bulk.isPending}
              onClick={() => { setBulkMsg(null); bulk.mutate() }}
              className="h-9 px-4 rounded-lg bg-mint-500 text-white font-semibold inline-flex items-center gap-2 disabled:opacity-50 text-[13px]">
              {bulk.isPending
                ? <><Icon name="loader" className="w-4 h-4 animate-spin" /> Calculating…</>
                : <><Icon name="calendar-range" className="w-4 h-4" /> Calculate all months</>
              }
            </button>
          </div>
        </div>

        {bulkMsg && (
          <div className="mt-4 space-y-2">
            <div className="flex gap-4 text-[12px] text-ink-600">
              <span>Months: <strong>{bulkMsg.months_processed}</strong></span>
              <span className="text-emerald-600">OK: <strong>{bulkMsg.months_ok}</strong></span>
              {bulkMsg.months_skipped > 0 && <span className="text-amber-600">Skipped: <strong>{bulkMsg.months_skipped}</strong></span>}
              {bulkMsg.months_failed > 0  && <span className="text-rose-600">Failed: <strong>{bulkMsg.months_failed}</strong></span>}
              {bulkMsg.dry_run && <span className="text-ink-400 italic">dry-run — no writes</span>}
            </div>
            <table className="w-full text-[12px] border border-ink-100 rounded overflow-hidden">
              <thead className="bg-ink-50 text-ink-600 text-left">
                <tr>
                  <th className="px-3 py-1.5 font-semibold">Month</th>
                  <th className="px-3 py-1.5 font-semibold">Status</th>
                  <th className="px-3 py-1.5 font-semibold">Partners</th>
                  <th className="px-3 py-1.5 font-semibold text-right">Total payable</th>
                  <th className="px-3 py-1.5 font-semibold">Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {bulkMsg.results.map(row => (
                  <tr key={row.month} className={row.ok ? '' : 'bg-rose-50'}>
                    <td className="px-3 py-1.5 font-mono">{row.month}</td>
                    <td className="px-3 py-1.5">
                      {row.skipped
                        ? <span className="text-amber-600">skipped</span>
                        : row.ok
                          ? <span className="text-emerald-600">✓ ok</span>
                          : <span className="text-rose-600">✗ failed</span>
                      }
                    </td>
                    <td className="px-3 py-1.5">{row.partners_processed ?? '—'}</td>
                    <td className="px-3 py-1.5 text-right">
                      {row.total_payable_cents != null
                        ? `$${(row.total_payable_cents / 100).toFixed(2)}`
                        : '—'}
                    </td>
                    <td className="px-3 py-1.5 text-ink-500">{row.detail ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ── Filter bar ──────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <div className="text-[11px] uppercase text-ink-500 font-semibold mb-1">Partner</div>
          <select className={inp + ' w-44'} value={filterPartner} onChange={e => setFilterPartner(e.target.value)}>
{t('affiliates.all_partners')}
            {(partners.data?.partners ?? []).map(p =>
              <option key={p.id} value={String(p.id)}>{p.name}</option>
            )}
          </select>
        </div>
        <div>
          <div className="text-[11px] uppercase text-ink-500 font-semibold mb-1">Status</div>
          <select className={inp + ' w-36'} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
<option value="">{t('affiliates.all_statuses')}</option>
<option value="pending">{t('affiliates.status_pending')}</option>
<option value="approved">{t('affiliates.status_approved')}</option>
<option value="paid">{t('affiliates.status_paid')}</option>
<option value="voided">{t('affiliates.status_voided')}</option>
          </select>
        </div>
        <div>
          <div className="text-[11px] uppercase text-ink-500 font-semibold mb-1">Month</div>
          <input
            type="month"
            className={inp + ' w-40'}
            value={filterMonth}
            onChange={e => setFilterMonth(e.target.value)}
          />
        </div>
        {(filterPartner || filterStatus || filterMonth) && (
          <button
            onClick={clearFilters}
            className="h-9 px-3 rounded-lg border border-ink-200 text-ink-500 text-[13px] hover:bg-ink-50 self-end">
            Clear
          </button>
        )}
        <div className="ml-auto self-end flex items-center gap-2">
          <div className="text-[12px] text-ink-400">
            {filteredPayments.length} of {list.data?.payments.length ?? 0}
          </div>
          <a
            href={exportHref}
            download
            className="h-9 px-3 rounded-lg border border-ink-200 text-ink-700 font-semibold inline-flex items-center gap-1.5 text-[13px] hover:bg-ink-50">
            <Icon name="download" className="w-4 h-4" /> Export XLSX
          </a>
        </div>
      </div>

      {/* ── Bulk action bar ─────────────────────────────────────────── */}
      {checkedIds.size > 0 && (
        <div className="flex items-center gap-3 px-3 py-2 bg-royal-50 border border-royal-200 rounded-lg text-[13px]">
          <span className="font-semibold text-royal-800">{checkedIds.size} selected</span>
          <button
            disabled={bulkApprove.isPending}
            onClick={() => {
              const pendingIds = filteredPayments
                .filter(p => checkedIds.has(p.id) && p.status === 'pending')
                .map(p => p.id)
              if (!pendingIds.length) { setGenMsg('No pending payments in selection.'); return }
              setGenMsg(null); bulkApprove.mutate(pendingIds)
            }}
            className="h-8 px-3 rounded-lg bg-royal-500 text-white font-semibold disabled:opacity-50 inline-flex items-center gap-1">
            <Icon name="check" className="w-3.5 h-3.5" />
            {bulkApprove.isPending ? 'Approving…' : 'Approve pending'}
          </button>
          <button
            disabled={bulkVoid.isPending}
            onClick={() => {
              const voidableIds = filteredPayments
                .filter(p => checkedIds.has(p.id) && p.status !== 'paid')
                .map(p => p.id)
              if (!voidableIds.length) { setGenMsg('No voidable payments in selection (paid rows cannot be voided).'); return }
              if (!confirm(`Void ${voidableIds.length} payment(s)? This cannot be undone.`)) return
              setGenMsg(null); bulkVoid.mutate(voidableIds)
            }}
            className="h-8 px-3 rounded-lg border border-rose-300 text-rose-700 font-semibold disabled:opacity-50 inline-flex items-center gap-1 hover:bg-rose-50">
            <Icon name="x" className="w-3.5 h-3.5" />
            {bulkVoid.isPending ? 'Voiding…' : 'Void selected'}
          </button>
          {(() => {
            const voidedIds = filteredPayments
              .filter(p => checkedIds.has(p.id) && p.status === 'voided')
              .map(p => p.id)
            if (!voidedIds.length) return null
            return deleteConfirmPending ? (
              <button
                disabled={bulkDelete.isPending}
                onClick={() => { setGenMsg(null); bulkDelete.mutate(voidedIds) }}
                className="h-8 px-3 rounded-lg bg-rose-600 text-white font-semibold disabled:opacity-50 inline-flex items-center gap-1 animate-pulse">
                <Icon name="trash-2" className="w-3.5 h-3.5" />
                {bulkDelete.isPending ? 'Deleting…' : `Confirm delete ${voidedIds.length}`}
              </button>
            ) : (
              <button
                onClick={() => setDeleteConfirmPending(true)}
                className="h-8 px-3 rounded-lg border border-rose-400 text-rose-700 font-semibold inline-flex items-center gap-1 hover:bg-rose-50">
                <Icon name="trash-2" className="w-3.5 h-3.5" />
                Delete voided ({voidedIds.length})
              </button>
            )
          })()}
          <button
            onClick={() => { setCheckedIds(new Set()); setDeleteConfirmPending(false) }}
            className="ml-auto h-8 px-3 rounded-lg border border-ink-200 text-ink-500 hover:bg-ink-50">
            Clear selection
          </button>
        </div>
      )}

      <Card className="overflow-hidden">
        <table className="w-full text-[13px]">
          <thead className="bg-ink-50 text-ink-600 text-left">
            <tr>
              <th className="px-3 py-2 w-8">
                <input
                  type="checkbox"
                  checked={filteredPayments.length > 0 && checkedIds.size === filteredPayments.length}
                  onChange={() => toggleAll(filteredPayments)}
                  className="rounded"
                />
              </th>
              <th className="px-3 py-2 font-semibold">Partner</th>
              <th className="px-3 py-2 font-semibold">Period</th>
              <th className="px-3 py-2 font-semibold">Model</th>
              <th className="px-3 py-2 font-semibold text-right">Customers</th>
              <th className="px-3 py-2 font-semibold text-right">Shipments</th>
              <th className="px-3 py-2 font-semibold text-right">Payable</th>
              <th className="px-3 py-2 font-semibold">Status</th>
              <th className="px-3 py-2 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredPayments.map(p => (
              <tr key={p.id} className={`border-t border-ink-100 hover:bg-ink-50/50 ${checkedIds.has(p.id) ? 'bg-royal-50/40' : ''}`}>
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={checkedIds.has(p.id)}
                    onChange={() => toggleCheck(p.id)}
                    className="rounded"
                  />
                </td>
                <td className="px-3 py-2 font-semibold">{p.partner_name}</td>
                <td className="px-3 py-2 text-ink-700">{p.period_start} → {p.period_end}</td>
                <td className="px-3 py-2 text-ink-700">
                  {p.model_type_applied === 'profit_pct'
                    ? `${parseFloat(p.rate_applied)}% profit`
                    : `${parseFloat(p.rate_applied)}¢/label`}
                </td>
                <td className="px-3 py-2 text-right">{p.customer_count}</td>
                <td className="px-3 py-2 text-right">{p.shipment_count}</td>
                <td className="px-3 py-2 text-right font-semibold">{usd(p.payable_usd)}</td>
                <td className="px-3 py-2"><span className={statusPill(p.status)}>{p.status}</span></td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => setSelected(p)}
                    className="h-8 px-2 rounded text-[12.5px] hover:bg-ink-100 inline-flex items-center gap-1">
                    <Icon name="eye" className="w-3.5 h-3.5" /> View
                  </button>
                  {p.xlsx_path && (
                    <DownloadLink href={`/api/affiliates/payments/${p.id}/xlsx`}
                      className="h-8 px-2 rounded text-[12.5px] hover:bg-ink-100 inline-flex items-center gap-1 text-mint-700">
                      <Icon name="file-spreadsheet" className="w-3.5 h-3.5" /> XLSX
                    </DownloadLink>
                  )}
                  {p.status === 'voided' && (
                    <button
                      disabled={deletePayment.isPending}
                      onClick={() => {
                        if (!confirm('Delete this voided payment?')) return
                        deletePayment.mutate(p.id)
                      }}
                      className="h-8 px-2 rounded text-[12.5px] hover:bg-rose-50 text-rose-600 inline-flex items-center gap-1 disabled:opacity-50">
                      <Icon name="x" className="w-3.5 h-3.5" /> Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {filteredPayments.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-ink-400">
                {list.data?.payments.length
                  ? 'No payments match the current filters.'
                  : 'No payments yet. Generate one above.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {selected && <PaymentDetail payment={selected} onClose={() => setSelected(null)}
        onChanged={() => {
          qc.invalidateQueries({ queryKey: ['affiliates', 'payments'] })
          qc.invalidateQueries({ queryKey: ['affiliates', 'scorecard'] })
          setSelected(null)
        }}
      />}
    </div>
  )
}

function PaymentDetail({ payment, onClose, onChanged }: { payment: Payment; onClose: () => void; onChanged: () => void }) {
  const qc = useQueryClient()
  const detail = useQuery<{ payment: Payment; lines: PaymentLine[] }>({
    queryKey: ['affiliates', 'payment', payment.id],
    queryFn: () => fetch(`/api/affiliates/payments/${payment.id}`).then(r => r.json()),
  })
  const instQuery = useQuery<{ installments: Installment[] }>({
    queryKey: ['affiliates', 'payment', payment.id, 'installments'],
    queryFn: () => fetch(`/api/affiliates/payments/${payment.id}/installments`).then(r => r.json()),
  })

  const [busyAction, setBusyAction] = useState<string | null>(null)
  const [actionErr, setActionErr] = useState<string | null>(null)

  // Add-installment form state
  const [showInstForm, setShowInstForm] = useState(false)
  const [instType,   setInstType]   = useState<string>('ach')
  const [instAmount, setInstAmount] = useState('')
  const [instRef,    setInstRef]    = useState('')
  const [instDate,   setInstDate]   = useState(todayIso())
  const [instChannel,setInstChannel]= useState('chase')
  const [instNotes,  setInstNotes]  = useState('')
  const [instBusy,   setInstBusy]   = useState(false)
  const [instErr,    setInstErr]    = useState<string | null>(null)

  async function approve() {
    setBusyAction('approve'); setActionErr(null)
    const r = await fetch(`/api/affiliates/payments/${payment.id}/approve`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) })
    setBusyAction(null)
    if (!r.ok) { setActionErr(`HTTP ${r.status}`); return }
    onChanged()
  }

  async function addInstallment() {
    const amountCents = Math.round(Number(instAmount) * 100)
    if (!instRef || !instAmount || isNaN(amountCents) || amountCents <= 0) {
      setInstErr('Amount and reference are required'); return
    }
    setInstBusy(true); setInstErr(null)
    const r = await fetch(`/api/affiliates/payments/${payment.id}/installments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        transfer_type: instType,
        amount_cents:  amountCents,
        reference:     instRef,
        paid_at:       instDate ? instDate + 'T00:00:00Z' : undefined,
        channel:       instChannel,
        notes:         instNotes || null,
      }),
    })
    setInstBusy(false)
    if (!r.ok) { setInstErr(`HTTP ${r.status}`); return }
    // Reset form
    setInstAmount(''); setInstRef(''); setInstDate(todayIso())
    setInstNotes(''); setShowInstForm(false)
    qc.invalidateQueries({ queryKey: ['affiliates', 'payment', payment.id, 'installments'] })
    qc.invalidateQueries({ queryKey: ['affiliates', 'payment', payment.id] })
    qc.invalidateQueries({ queryKey: ['affiliates', 'payments'] })
    qc.invalidateQueries({ queryKey: ['affiliates', 'scorecard'] })
  }

  async function deleteInstallment(iid: number) {
    if (!confirm('Remove this installment record?')) return
    await fetch(`/api/affiliates/payments/${payment.id}/installments/${iid}`, { method: 'DELETE' })
    qc.invalidateQueries({ queryKey: ['affiliates', 'payment', payment.id, 'installments'] })
  }

  async function voidPayment() {
    if (!confirm('Void this payment? It cannot be un-voided.')) return
    setBusyAction('void'); setActionErr(null)
    const r = await fetch(`/api/affiliates/payments/${payment.id}/void`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) })
    setBusyAction(null)
    if (!r.ok) { setActionErr(`HTTP ${r.status}`); return }
    onChanged()
  }

  const p = detail.data?.payment ?? payment
  const lines = detail.data?.lines ?? []
  const installments = instQuery.data?.installments ?? []
  const installedTotal = installments.reduce((s, i) => s + Number(i.amount_cents), 0)

  return (
    <div className="fixed inset-0 bg-black/30 z-40 flex items-center justify-center" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-xl w-[820px] max-h-[90vh] overflow-y-auto shadow-xl">

        {/* Header */}
        <div className="p-5 border-b border-ink-100 flex items-center justify-between">
          <div>
            <div className="text-[18px] font-bold">{p.partner_name}</div>
            <div className="text-[12px] text-ink-500">
              {p.period_start} → {p.period_end} · {p.model_type_applied === 'profit_pct'
                ? `${parseFloat(p.rate_applied)}% of profit`
                : `${parseFloat(p.rate_applied)}¢ per label`}
              {' '}<span className={statusPill(p.status)}>{p.status}</span>
            </div>
          </div>
          <button onClick={onClose} className="h-8 w-8 rounded hover:bg-ink-100 inline-flex items-center justify-center">
            <Icon name="x" className="w-4 h-4" />
          </button>
        </div>

        {/* Summary metrics */}
        <div className="p-5 grid grid-cols-3 gap-4 border-b border-ink-100">
          <Metric label="Gross"            value={usd(p.gross_usd)} />
          <Metric label="Held (below min)" value={usd(p.held_usd)} accent="amber" />
          <Metric label="Payable"          value={usd(p.payable_usd)} accent="mint" />
        </div>

        {/* Per-customer breakdown */}
        <div className="p-5 border-b border-ink-100">
          <div className="text-[13px] font-semibold mb-2">Per-customer breakdown</div>
          <div className="border border-ink-100 rounded overflow-hidden">
            <table className="w-full text-[12.5px]">
              <thead className="bg-ink-50 text-ink-600 text-left">
                <tr>
                  <th className="px-3 py-1.5 font-semibold">Customer</th>
                  <th className="px-3 py-1.5 font-semibold">Metric</th>
                  <th className="px-3 py-1.5 font-semibold text-right">Units</th>
                  <th className="px-3 py-1.5 font-semibold text-right">Rate</th>
                  <th className="px-3 py-1.5 font-semibold text-right">Shipments</th>
                  <th className="px-3 py-1.5 font-semibold text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {lines.map(l => (
                  <tr key={l.id} className="border-t border-ink-100">
                    <td className="px-3 py-1.5 font-mono text-[11.5px]">{l.customer_name}</td>
                    <td className="px-3 py-1.5">{l.metric_type}</td>
                    <td className="px-3 py-1.5 text-right">{Number(l.units).toLocaleString()}</td>
                    <td className="px-3 py-1.5 text-right">{l.rate}</td>
                    <td className="px-3 py-1.5 text-right">{l.shipment_count}</td>
                    <td className="px-3 py-1.5 text-right font-semibold">{usd(Number(l.amount_cents) / 100)}</td>
                  </tr>
                ))}
                {!lines.length && <tr><td colSpan={6} className="px-3 py-4 text-center text-ink-400">No lines.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Payment installments section ───────────────────────────── */}
        <div className="p-5 border-b border-ink-100">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[13px] font-semibold">
              Payment installments
              {installments.length > 0 && (
                <span className="ml-2 text-[12px] font-normal text-ink-500">
                  {installments.length} record(s) · {usd(installedTotal / 100)} paid
                  {installedTotal < p.payable_usd * 100 && (
                    <span className="text-amber-600 ml-2">
                      ({usd((p.payable_usd * 100 - installedTotal) / 100)} remaining)
                    </span>
                  )}
                </span>
              )}
            </div>
            {p.status !== 'voided' && (
              <button
                onClick={() => setShowInstForm(s => !s)}
                className="h-8 px-3 rounded-lg border border-ink-200 text-[12.5px] inline-flex items-center gap-1 hover:bg-ink-50">
                <Icon name="plus" className="w-3.5 h-3.5" /> Add installment
              </button>
            )}
          </div>

          {/* Existing installments */}
          {installments.length > 0 && (
            <div className="border border-ink-100 rounded overflow-hidden mb-3">
              <table className="w-full text-[12.5px]">
                <thead className="bg-ink-50 text-ink-600 text-left">
                  <tr>
                    <th className="px-3 py-1.5 font-semibold">Type</th>
                    <th className="px-3 py-1.5 font-semibold">Channel</th>
                    <th className="px-3 py-1.5 font-semibold">Reference</th>
                    <th className="px-3 py-1.5 font-semibold">Date</th>
                    <th className="px-3 py-1.5 font-semibold text-right">Amount</th>
                    <th className="px-3 py-1.5 w-8" />
                  </tr>
                </thead>
                <tbody>
                  {installments.map(inst => (
                    <tr key={inst.id} className="border-t border-ink-100">
                      <td className="px-3 py-1.5">
                        <span className="uppercase text-[11px] font-semibold tracking-wide bg-ink-100 text-ink-700 px-1.5 py-0.5 rounded">
                          {inst.transfer_type}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-ink-600">{inst.channel}</td>
                      <td className="px-3 py-1.5 font-mono text-[11.5px]">{inst.reference}</td>
                      <td className="px-3 py-1.5 text-ink-600">
                        {new Date(inst.paid_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                      <td className="px-3 py-1.5 text-right font-semibold text-mint-700">
                        {usd(Number(inst.amount_cents) / 100)}
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        <button
                          onClick={() => deleteInstallment(inst.id)}
                          className="h-6 w-6 rounded hover:bg-rose-50 inline-flex items-center justify-center text-rose-400 hover:text-rose-600">
                          <Icon name="x" className="w-3 h-3" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {installments.length === 0 && !showInstForm && (
            <div className="text-[12.5px] text-ink-400 italic">No payment installments recorded yet.</div>
          )}

          {/* Add installment form */}
          {showInstForm && (
            <div className="border border-ink-200 rounded-lg p-4 bg-ink-50/50 space-y-3">
              <div className="text-[12.5px] font-semibold text-ink-700">New installment</div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Transfer type">
                  <select className={inp} value={instType} onChange={e => setInstType(e.target.value)}>
                    <option value="ach">ACH</option>
                    <option value="zelle">Zelle</option>
                    <option value="wire">Wire</option>
                    <option value="deposit">Deposit to account</option>
                    <option value="check">Check</option>
                  </select>
                </Field>
                <Field label="Channel">
                  <select className={inp} value={instChannel} onChange={e => setInstChannel(e.target.value)}>
                    <option value="chase">Chase</option>
                  </select>
                </Field>
                <Field label="Amount (USD)">
                  <input className={inp} type="number" step="0.01" min="0.01" placeholder="979.50"
                    value={instAmount} onChange={e => setInstAmount(e.target.value)} />
                </Field>
                <Field label="Reference #">
                  <input className={inp} placeholder="ACH trace / Zelle confirmation / check #"
                    value={instRef} onChange={e => setInstRef(e.target.value)} />
                </Field>
                <Field label="Date">
                  <input type="date" className={inp} value={instDate} onChange={e => setInstDate(e.target.value)} />
                </Field>
                <Field label="Notes (optional)">
                  <input className={inp} placeholder="Installment 1 of 2, etc."
                    value={instNotes} onChange={e => setInstNotes(e.target.value)} />
                </Field>
              </div>
              {instErr && <div className="text-rose-600 text-[12.5px]">{instErr}</div>}
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => { setShowInstForm(false); setInstErr(null) }}
                  className="h-8 px-3 rounded-lg border border-ink-200 text-[12.5px]">Cancel</button>
                <button
                  disabled={instBusy || !instAmount || !instRef}
                  onClick={addInstallment}
                  className="h-8 px-4 rounded-lg bg-mint-500 text-white font-semibold text-[12.5px] disabled:opacity-50">
                  {instBusy ? 'Saving…' : 'Save installment'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Action bar */}
        <div className="p-5 space-y-2">
          {actionErr && <div className="text-rose-600 text-[13px]">{actionErr}</div>}
          <div className="flex items-center gap-2">
            {p.status === 'pending' && (
              <button disabled={!!busyAction} onClick={approve}
                className="h-9 px-3 rounded-lg bg-royal-500 text-white font-semibold text-[13px] disabled:opacity-50">
                {busyAction === 'approve' ? 'Approving…' : 'Approve'}
              </button>
            )}
            {(p.status === 'pending' || p.status === 'approved') && (
              <span className="text-[12px] text-ink-500">
                Use <em>Add installment</em> above to mark as paid with transfer details.
              </span>
            )}
            {p.status !== 'paid' && p.status !== 'voided' && (
              <button disabled={!!busyAction} onClick={voidPayment}
                className="ml-auto h-9 px-3 rounded-lg border border-rose-300 text-rose-700 font-semibold text-[13px] disabled:opacity-50">
                {busyAction === 'void' ? 'Voiding…' : 'Void'}
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  )
}

